// components
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import DatePicker from '../DatePicker/DatePicker';
import { Icon } from '@sdb/planning-ui';

// utils
import { addDays, differenceInCalendarDays } from 'date-fns';

// types
import type { DatePickerProps } from '../DatePicker/DatePicker.types';

/**
 * An extension of DatePicker: every prop of the field itself is forwarded, so
 * the two can never drift apart. `sx` and `commitOnChange` are the stepper's
 * own (a fixed width and reporting a typed date at once), so they are not on
 * offer here.
 */
type DateStepperFieldProps = Omit<DatePickerProps, 'sx' | 'commitOnChange'> & {
  /** a stepper without a label leaves its two arrows unexplained */
  label: string;
  previousDayLabel: string;
  nextDayLabel: string;
  /** marks the field and its two steppers as one unit, for tests */
  testId?: string;
};

/**
 * A date picker flanked by ◀/▶ buttons that shift the date one day, so the
 * most common corrections (one day earlier/later) are a single click. The
 * steppers respect the min/max bounds: at a bound the button disables.
 */
const DateStepperField = ({
  date,
  minDate,
  maxDate,
  onChange,
  previousDayLabel,
  nextDayLabel,
  testId,
  size = 'small',
  variant = 'outlined',
  ...rest
}: DateStepperFieldProps) => {
  const step = (days: number) => {
    const base = date ?? new Date();
    onChange(addDays(base, days));
  };

  const atMin =
    !!minDate && !!date && differenceInCalendarDays(date, minDate) <= 0;
  const atMax =
    !!maxDate && !!date && differenceInCalendarDays(date, maxDate) >= 0;

  return (
    <Box
      data-testid={testId}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
    >
      {/* an arrow that cannot step is hidden, not greyed: it keeps its space
          so the field does not shift, but offers nothing to decode */}
      <Tooltip title={previousDayLabel}>
        <span style={atMin ? { visibility: 'hidden' } : undefined}>
          <IconButton
            size="small"
            aria-label={previousDayLabel}
            disabled={atMin}
            onClick={() => step(-1)}
          >
            <Icon type="chevron-left" fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <DatePicker
        {...rest}
        date={date}
        minDate={minDate}
        maxDate={maxDate}
        onChange={onChange}
        size={size}
        variant={variant}
        // the day count and the coupled date follow this field, so a typed
        // date must not wait for blur to take effect
        commitOnChange
        // a date is ten characters wide, so the roomy default only serves to
        // push two of these onto separate lines. 152px fits "31-07-2026" plus
        // the calendar button — measured, not guessed.
        sx={{ width: 152 }}
      />
      <Tooltip title={nextDayLabel}>
        <span style={atMax ? { visibility: 'hidden' } : undefined}>
          <IconButton
            size="small"
            aria-label={nextDayLabel}
            disabled={atMax}
            onClick={() => step(1)}
          >
            <Icon type="chevron-right" fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default DateStepperField;
